import React, { useState } from 'react';
import { Shield, Check, User, Mail } from 'lucide-react';
import QuizForm from './FreeQuizMagnet/QuizForm';

const FreeQuizMagnet = () => {
  const [showQuiz, setShowQuiz] = useState(false);
  const [userData, setUserData] = useState(null);

  const handleStartQuiz = () => {
    const nameInput = document.querySelector('input[name="name"]');
    const emailInput = document.querySelector('input[name="email"]');
    const coachingFocusSelect = document.querySelector('select[name="coachingFocus"]');

    if (!nameInput?.value || !emailInput?.value || !coachingFocusSelect?.value) {
      return;
    }

    const data = {
      name: nameInput.value,
      email: emailInput.value,
      coachingFocus: coachingFocusSelect.value
    };

    setUserData(data);
    setShowQuiz(true);
  };

  if (showQuiz && userData) {
    return <QuizForm userData={userData} />;
  }

  return (
    <section className="py-16 md:py-20 bg-white border-t border-green-100">
      <div className="max-w-2xl mx-auto px-4 md:px-6">
        {/* Problem Heading */}
        <div className="text-center mb-4">
          <h1 className="text-3xl md:text-5xl font-extrabold text-green-800 mb-3">DISCOVER THE HIDDEN GAPS HOLDING YOUR COACHING BUSINESS BACK</h1>
          <p className="text-lg md:text-2xl text-green-600 mb-6">Take our 30 second assessment to uncover exactly what's preventing your wellness practice from thriving in today's digital landscape.</p>
        </div>
        {/* Inviting Image */}
        <div className="flex justify-center mb-8">
          <img src="https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=600&q=80" alt="Wellness Quiz" className="rounded-xl shadow-lg w-full max-w-md object-cover" />
        </div>
        {/* Form Section */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-10">
          <h4 className="text-lg font-semibold text-green-800 mb-4">🔍 Get Your Personalized Business Assessment</h4>
          <div className="flex flex-col gap-4">
            <div className="flex items-center border rounded-lg px-3 py-2 bg-green-50">
              <User className="w-5 h-5 text-green-400 mr-2" />
              <input 
                type="text" 
                name="name" 
                placeholder="First Name" 
                className="bg-transparent flex-1 outline-none text-green-900" 
                required 
              />
            </div>
            <div className="flex items-center border rounded-lg px-3 py-2 bg-green-50">
              <Mail className="w-5 h-5 text-green-400 mr-2" />
              <input 
                type="email" 
                name="email" 
                placeholder="Email Address" 
                className="bg-transparent flex-1 outline-none text-green-900" 
                required 
              />
            </div>
            <div className="flex items-center border rounded-lg px-3 py-2 bg-green-50">
              <select 
                name="coachingFocus" 
                className="bg-transparent flex-1 outline-none text-green-900" 
                required 
                defaultValue=""
              >
                <option value="" disabled>Your Coaching Focus</option>
                <option value="Nutrition">Nutrition</option>
                <option value="Fitness">Fitness</option>
                <option value="Mindfulness">Mindfulness</option>
                <option value="Holistic Health">Holistic Health</option>
                <option value="Life Coaching">Life Coaching</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <button 
              type="button" 
              onClick={handleStartQuiz}
              className="mt-2 px-6 py-3 bg-green-500 text-white text-lg font-bold rounded-lg shadow-lg hover:bg-green-600 transition duration-300"
            >
              START MY ASSESSMENT
            </button>
          </div>
        </div>
        {/* Offer Section */}
        <div className="bg-green-50 rounded-xl shadow-lg overflow-hidden mb-10 md:mb-14">
          <div className="p-6 md:p-8 flex flex-col items-center">
            <Shield className="w-10 h-10 text-green-400 mb-4" strokeWidth={1.5} />
            <h3 className="text-xl font-bold text-green-800 mb-2">What You'll Discover:</h3>
            <ul className="mt-4 space-y-2 text-left">
              <li className="flex items-start"><Check className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" /><span className="text-green-700 text-sm">The specific tech bottlenecks costing you clients</span></li>
              <li className="flex items-start"><Check className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" /><span className="text-green-700 text-sm">Where your business ranks against successful wellness coaches</span></li>
              <li className="flex items-start"><Check className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" /><span className="text-green-700 text-sm">Your personalized AI opportunity score</span></li>
              <li className="flex items-start"><Check className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" /><span className="text-green-700 text-sm">The exact tools that can automate your biggest time-wasters</span></li>
            </ul>
          </div>
        </div>
        {/* Testimonials Section */}
        <div className="mt-12">
          <h4 className="text-xl font-bold text-green-800 mb-6 text-center">What Smart Coaches Are Saying</h4>
          <div className="relative">
            <div className="flex overflow-x-auto gap-4 pb-4 snap-x snap-mandatory scrollbar-hide">
              <div className="flex-none w-[80%] snap-start">
                <div className="bg-green-50 rounded-lg p-5 shadow flex flex-col h-full">
                  <p className="text-green-900 italic mb-2">"This assessment revealed I was spending 15+ hours weekly on tasks AI could handle. Implementing the recommendations has doubled my client base while working fewer hours."</p>
                  <span className="text-green-700 font-semibold">— Sarah M., Nutrition Coach</span>
                </div>
              </div>
              <div className="flex-none w-[80%] snap-start">
                <div className="bg-green-50 rounded-lg p-5 shadow flex flex-col h-full">
                  <p className="text-green-900 italic mb-2">"The insights from this assessment helped me identify exactly where I was losing potential clients. Now my conversion rate is up 40%!"</p>
                  <span className="text-green-700 font-semibold">— Michael T., Fitness Coach</span>
                </div>
              </div>
              <div className="flex-none w-[80%] snap-start">
                <div className="bg-green-50 rounded-lg p-5 shadow flex flex-col h-full">
                  <p className="text-green-900 italic mb-2">"I was skeptical at first, but this assessment pinpointed exactly where my business was leaking money. The ROI has been incredible."</p>
                  <span className="text-green-700 font-semibold">— Lisa R., Wellness Consultant</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FreeQuizMagnet; 